import { useState } from 'react';
import Logo from '../Logo';
import UserAvatar from '../UserAvatar';
import MobileSheet from './MobileSheet';
import ConfirmModal from '../ConfirmModal';
import { LogOutIcon, PlusIcon, FolderKanbanIcon, CheckIcon, ChevronRightIcon, DeleteIcon, LayoutGridIcon, ZapIcon, RefreshIcon } from './Icons';
import ProjectTypeFilterBar from '../shared/ProjectTypeFilterBar';
import { PROJECT_TYPE_CONFIG } from '../../constants';
import useProjectDashboard from '../../hooks/useProjectDashboard';
import './mobile.css';

export default function ProjectDashboardMobile({ user, userProfile, users, onSelectProject, onLogout, showToast, refreshUsers }) {
  const {
    projects, filteredProjects, loading, migrating, deleteTarget, setDeleteTarget,
    typeFilter, setTypeFilter, isAdmin, handleCreateProject, handleDeleteProject,
  } = useProjectDashboard({ user, userProfile, showToast });

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createType, setCreateType] = useState('scrum');
  const [createMembers, setCreateMembers] = useState([]);

  const toggleMember = (uid) => {
    setCreateMembers((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const handleCreate = async () => {
    if (!createName.trim()) return;
    try {
      await handleCreateProject({
        name: createName.trim(),
        description: createDesc.trim(),
        type: createType,
        members: createMembers,
      });
      setShowCreate(false);
      setCreateName('');
      setCreateDesc('');
      setCreateType('scrum');
      setCreateMembers([]);
      showToast('Project created', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to create project', 'error');
    }
  };

  if (loading || migrating) {
    return (
      <div className="mobile-page" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="mobile-loading-spinner" />
        <p>{migrating ? 'Migrating data...' : 'Loading projects...'}</p>
      </div>
    );
  }

  return (
    <div className="mobile-page mobile-project-page">
      <div className="mobile-sprint-header">
        <Logo size="small" showTagline={false} />
        <div className="mobile-sprint-info">
          <strong>Projects</strong>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button className="mobile-btn-icon" onClick={() => {}} title="My Profile">
            <UserAvatar user={userProfile} size={28} showStatus={false} />
          </button>
          <button className="mobile-btn-icon" onClick={onLogout}>
            <LogOutIcon size={20} />
          </button>
        </div>
      </div>

      <div className="mobile-sprint-body">
        {projects.length > 0 && (
          <ProjectTypeFilterBar typeFilter={typeFilter} onChange={setTypeFilter} iconSize={13} />
        )}
        {projects.length === 0 ? (
          <div className="mobile-project-empty">
            <FolderKanbanIcon size={40} />
            <h3>No projects yet</h3>
            <p>{isAdmin ? 'Create your first project to get started.' : 'Ask your admin to add you to a project.'}</p>
          </div>
        ) : (
          filteredProjects.map((project) => {
            const memberUsers = users.filter((u) => project.members?.includes(u.uid));
            const projType = project.type || 'scrum';
            const isKanban = projType === 'kanban';
            const isRetro = projType === 'retrospective';
            return (
              <div
                key={project.id}
                className="mobile-project-card"
                onClick={() => onSelectProject(project)}
              >
                <div className="mobile-project-card-top">
                  <div className={`mobile-project-card-icon ${isKanban ? 'mobile-project-card-icon--kanban' : isRetro ? 'mobile-project-card-icon--retro' : ''}`}>
                    {isKanban ? <ZapIcon size={20} /> : isRetro ? <RefreshIcon size={20} /> : <LayoutGridIcon size={20} />}
                  </div>
                  <div className="mobile-project-card-info">
                    <div className="mobile-project-card-name">{project.name}</div>
                    {project.description && (
                      <div className="mobile-project-card-desc">{project.description}</div>
                    )}
                  </div>
                  {isAdmin && (
                    <button
                      className="mobile-btn-icon mobile-project-card-delete"
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(project.id); }}
                      title="Delete project"
                    >
                      <DeleteIcon size={14} />
                    </button>
                  )}
                  <div className="mobile-project-card-arrow">
                    <ChevronRightIcon size={18} />
                  </div>
                </div>
                <div className="mobile-project-card-footer">
                  <div className="mobile-project-card-members">
                    {memberUsers.slice(0, 4).map((u) => (
                      <UserAvatar key={u.uid} user={u} size={20} showStatus={false} />
                    ))}
                    {memberUsers.length > 4 && (
                      <span className="mobile-project-card-members-more">+{memberUsers.length - 4}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className={`project-type-chip ${isKanban ? 'project-type-chip--kanban' : isRetro ? 'project-type-chip--retro' : 'project-type-chip--scrum'}`}>
                      {isKanban ? <><ZapIcon size={11} /> Kanban</> : isRetro ? <><RefreshIcon size={11} /> Retro</> : <><LayoutGridIcon size={11} /> Scrum</>}
                    </span>
                    <span className="mobile-project-card-member-count">
                      {memberUsers.length} member{memberUsers.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {isAdmin && (
        <div className="mobile-fab-group" style={{ bottom: '24px' }}>
          <button className="mobile-fab" onClick={() => setShowCreate(true)}>
            <PlusIcon size={24} />
          </button>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          message="Delete this project and ALL its sprints and items? This cannot be undone."
          onConfirm={handleDeleteProject}
          onCancel={() => setDeleteTarget(null)}
          confirmLabel="Delete Project"
        />
      )}

      {showCreate && (
        <MobileSheet title="Create Project" onClose={() => setShowCreate(false)}>
          <div className="mobile-form">
            <div className="mobile-input-group">
              <label className="mobile-label">Project Name</label>
              <input
                className="mobile-input"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g. Website Redesign"
              />
            </div>
            <div className="mobile-input-group">
              <label className="mobile-label">Description</label>
              <textarea
                className="mobile-input"
                rows={2}
                value={createDesc}
                onChange={(e) => setCreateDesc(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="mobile-input-group">
              <label className="mobile-label">Board Type</label>
              <div className="project-type-selector">
                {Object.entries(PROJECT_TYPE_CONFIG).map(([key, cfg]) => (
                  <div
                    key={key}
                    className={`project-type-option ${createType === key ? 'selected' : ''}`}
                    onClick={() => setCreateType(key)}
                  >
                    <strong>{cfg.label}</strong>
                    <span>{cfg.description}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mobile-input-group">
              <label className="mobile-label">Add Members</label>
              <div className="mobile-project-member-picker">
                {users.map((u) => {
                  const selected = createMembers.includes(u.uid);
                  return (
                    <div
                      key={u.uid}
                      className={`mobile-project-member-option ${selected ? 'selected' : ''}`}
                      onClick={() => toggleMember(u.uid)}
                    >
                      <UserAvatar user={u} size={22} showStatus={false} />
                      <span>{u.displayName}</span>
                      {selected && <CheckIcon size={14} />}
                    </div>
                  );
                })}
              </div>
            </div>
            <button
              className="mobile-btn mobile-btn-primary"
              onClick={handleCreate}
              disabled={!createName.trim()}
            >
              Create Project
            </button>
          </div>
        </MobileSheet>
      )}
    </div>
  );
}
